import { Routes } from '@angular/router';
import { ClientsComponent } from './pages/clients/clients.component';
import { ProduitsComponent } from './pages/produits/produits.component';
import { AccueilComponent } from './pages/accueil/accueil.component';
import { ProfileComponent } from './pages/profile/profile.component';
import { CommandeComponent } from './pages/commande/commande.component';
import { SignupComponent } from './pages/signup/signup.component';
import { LoginComponent } from './pages/login/login.component';
import { CompleteSignupComponent } from './pages/complete-signup/complete-signup.component';
import { ChangePasswordComponent } from './pages/change-password/change-password.component';
import { TestComponent } from './pages/test/test.component';
import { LoadingComponent } from './pages/loading/loading.component';
// Importer le guard pour protéger les routes
import { authGuard, onboardingRedirectGuard, rootRedirectGuard, staffGuard } from './services/auth.guard';
import { Page404Component } from './pages/page404/page404.component';
import { DeliveryComponent } from './pages/delivery/delivery.component';
import { HomeComponent } from './pages/home/home.component';
import { CalculatorComponent } from './pages/calculator/calculator.component';
import { GestionnaireComponent } from './pages/gestionnaire/gestionnaire.component';
import { StoreComponent  } from './pages/Store/store.component';
import { EmployeesComponent } from './pages/employe/employe.component'; 
import { NavbarComponent } from './shared/navbar/navbar.component';
import { WelcomeComponent } from './pages/Welcom/welcom/welcome.component';
import { MessagesComponent } from './pages/fb_messages/messages.component';
import { SettingsComponent } from './pages/settings/settings/settings.component';
import { ActivitiesComponent } from './pages/activities/activities/activities.component';
import { AuthCallbackComponent } from './pages/auth-callback/auth-callback.component';


export const routes: Routes = [
  // Routes publiques
  
  // Racine : si user connecté → redirige vers /Accueil (dashboard refondu),
  // sinon → WelcomeComponent (landing publique).
  { path: '', component: WelcomeComponent, canActivate: [rootRedirectGuard] },
  { path: 'login', component: LoginComponent },
  { path: 'signup', component: SignupComponent },
  { path: 'auth/callback', component: AuthCallbackComponent },
  {
    path: 'auth/landing',
    loadComponent: () =>
      import('./pages/auth-landing/auth-landing.component').then(
        (m) => m.AuthLandingComponent,
      ),
  },
  { path: 'CompleteSignUp', component: CompleteSignupComponent },
  {
    path: 'changer-mot-de-passe/:token',
   component:ChangePasswordComponent
  },
  { path: 'mot-de-passe-oublie', component: TestComponent },
  { path: 'loading', component: LoadingComponent },
  { path: 'PageNotFound', component: Page404Component },

  // ── Pages légales PUBLIQUES (confidentialité, CGU, suppression) ──
  // Référencées par les formulaires Meta lead ads (privacy_policy_url) :
  // /privacy et /confidentialite DOIVENT rester des URL valides.
  {
    path: 'legal/:kind',
    loadComponent: () =>
      import('./pages/legal/legal.component').then((m) => m.LegalPagesComponent),
  },
  { path: 'legal', redirectTo: 'legal/privacy' },
  { path: 'privacy', redirectTo: 'legal/privacy' },
  { path: 'confidentialite', redirectTo: 'legal/privacy' },
  { path: 'cgu', redirectTo: 'legal/terms' },
  { path: 'terms', redirectTo: 'legal/terms' },

  // ── POS écran verrou TERMINAL (PUBLIC, hors authGuard/layout) ──
  // Appareil enrôlé par le vendeur ; un agent s'attache par code + PIN.
  {
    path: 'pos/terminal',
    loadComponent: () =>
      import('./pages/pos/pos-terminal-lock/pos-terminal-lock.component').then(
        (m) => m.PosTerminalLockComponent,
      ),
  },

  // ── Créneau agent via token de livraison (PUBLIC, hors authGuard) ──
  // Ouvert depuis le QR / lien email / WhatsApp reçu par l'agent. Le token
  // tient lieu de secret ; la page affiche le créneau + code agent puis
  // renvoie vers /pos/terminal pour l'attache (code + PIN).
  {
    path: 'agents/shift/:token',
    loadComponent: () =>
      import('./pages/agents/shift-landing/shift-landing.component').then(
        (m) => m.ShiftLandingComponent,
      ),
  },

  // ── Terminal agent terrain modulaire (PUBLIC, hors authGuard/layout) ──
  // Shell composé selon les rôles de l'agent (prep/stock/delivery/support +
  // CTA caisse). L'agent s'attache via son code + PIN (session virtuelle, sans
  // appareil enrôlé) ; le token de créneau dans l'URL tient lieu de 1er facteur.
  {
    path: 'agents/terminal/:token',
    loadComponent: () =>
      import('./pages/agents/agent-terminal/agent-terminal.component').then(
        (m) => m.AgentTerminalComponent,
      ),
  },

  // ── Telegram Mini App (PUBLIC, hors authGuard/layout) ──
  // Page plein écran ouverte DANS Telegram (bouton menu / web_app du bot).
  // Auth = validation HMAC de l'initData côté serveur (X-Telegram-Init-Data).
  {
    path: 'tg/app',
    loadComponent: () =>
      import('./pages/telegram/miniapp/miniapp.component').then(
        (m) => m.TelegramMiniappComponent,
      ),
  },

  // ── Course livreur interne (PUBLIC, hors authGuard/layout) ──
  // Le livreur reçoit ce lien par WhatsApp : statut, preuve photo, COD.
  // Le jeton opaque par expédition tient lieu de secret (TTL + rate-limit
  // côté API /public/courier/*, proxifié same-origin par nginx).
  {
    path: 'course/:token',
    loadComponent: () =>
      import('./pages/courier-course/courier-course.component').then(
        (m) => m.CourierCourseComponent,
      ),
  },

  // ── Social Order Checkout (PUBLIC, hors authGuard/layout) ──
  // Mini web-app de prise de commande ouverte depuis Messenger / Instagram /
  // Telegram / WhatsApp via le widget envoyé par le chatbot. Token URL-safe
  // (32 chars) sert d'identifiant — pas d'authentification utilisateur.
  // Le client choisit ses produits, saisit son adresse (carte + GPS via
  // vd-address-picker) et finalise la commande sans quitter le chat.
  {
    path: 'social-checkout/:token',
    loadComponent: () =>
      import('./pages/social-checkout/social-checkout.component').then(
        (m) => m.SocialCheckoutComponent,
      ),
  },

  // ── Fiche publique d'un deep-link scan signé HMAC (PUBLIC, hors authGuard) ──
  // Ouverte en scannant un QR « Lien public » (produit / variante / suivi
  // commande ou colis). Appelle GET {apiUrl}/public/scan/{token} qui vérifie
  // la signature et renvoie une fiche réduite. Sans cette route, le lien
  // retombait sur le SPA sans handler → 404 in-app.
  {
    path: 'public/scan/:token',
    loadComponent: () =>
      import('./pages/public-scan/public-scan.component').then(
        (m) => m.PublicScanComponent,
      ),
  },

  // ── Espace STAFF vendeur.site (PUBLIC hors authGuard/layout vendeur) ──
  // Accès réseau imposé au bord par Traefik (vpn-only@file sur PathPrefix
  // /staff) + require_vpn côté API. Le login est public DANS le VPN ; la
  // console est protégée par staffGuard (rôle staff/admin). Un staff appartient
  // à la plateforme (vendor_id NULL), à la différence des agents (vendeur).
  {
    path: 'staff/login',
    loadComponent: () =>
      import('./pages/staff/staff-login/staff-login.component').then(
        (m) => m.StaffLoginComponent,
      ),
  },
  {
    path: 'staff',
    canActivate: [staffGuard],
    loadComponent: () =>
      import('./pages/staff/staff-layout/staff-layout.component').then(
        (m) => m.StaffLayoutComponent,
      ),
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./pages/staff/staff-dashboard/staff-dashboard.component').then(
            (m) => m.StaffDashboardComponent,
          ),
      },
      {
        path: 'team',
        loadComponent: () =>
          import('./pages/staff/staff-team/staff-team.component').then(
            (m) => m.StaffTeamComponent,
          ),
      },
      {
        path: 'onboarding',
        loadComponent: () =>
          import('./pages/staff/staff-onboarding/staff-onboarding.component').then(
            (m) => m.StaffOnboardingComponent,
          ),
      },
    ],
  },


  // Routes protégées
  {
    path: '',
    component: HomeComponent,  // Le layout avec Sidebar et Navbar
    canActivate: [authGuard],  // Le guard s'applique à toutes les routes à l'intérieur de ce layout
    children: [
      { path: 'Accueil', component: AccueilComponent, canActivate: [onboardingRedirectGuard] },
      // Alias minuscule pour éviter le 404 si l'utilisateur tape /accueil
      { path: 'accueil', redirectTo: 'Accueil', pathMatch: 'full' },
      { path: 'clients', loadComponent: () => import('./pages/clients/clients.component').then(m => m.ClientsComponent) },
      { path: 'produits', loadComponent: () => import('./pages/produits/produits.component').then(m => m.ProduitsComponent) },
      { path: 'profile', loadComponent: () => import('./pages/profile/profile.component').then(m => m.ProfileComponent) },
      { path: 'commande', loadComponent: () => import('./pages/commande/commande.component').then(m => m.CommandeComponent) },
      { path: 'livreurs', loadComponent: () => import('./pages/delivery/delivery.component').then(m => m.DeliveryComponent) },
      // ── Écrans legacy dépréciés (unification expédition ship01) ──
      // tunisie-delivery (Intigo/Droppex) et mondial-delivery (Karrio brut)
      // sont remplacés par le hub /expeditions (CarrierShipment unifié).
      { path: 'tunisie-delivery', redirectTo: 'expeditions' },
      { path: 'mondial-delivery', redirectTo: 'expeditions' },
      { path: 'calculator', loadComponent: () => import('./pages/calculator/calculator.component').then(m => m.CalculatorComponent) },
      { path: 'Gestionnaire', loadComponent: () => import('./pages/gestionnaire/gestionnaire.component').then(m => m.GestionnaireComponent) },
      { path: 'comptabilite', loadComponent: () => import('./pages/comptabilite/comptabilite.component').then(m => m.ComptabiliteComponent) },
      { path: 'kimbyala', loadComponent: () => import('./pages/finance/kimbyala/kimbyala.component').then(m => m.KimbyalaComponent), data: { roles: ['vendor', 'admin'] } },
      { path: 'finance/kimbyala', redirectTo: 'kimbyala', pathMatch: 'full' },
      { path: 'Employe', loadComponent: () => import('./pages/employe/employe.component').then(m => m.EmployeesComponent) },
      { path: 'Boutique', loadComponent: () => import('./pages/Store/store.component').then(m => m.StoreComponent) },
      { path: 'navbar', component: NavbarComponent },
      { path: 'messages', loadComponent: () => import('./pages/fb_messages/messages.component').then(m => m.MessagesComponent) },
      { path: 'inbox', loadComponent: () => import('./pages/inbox/inbox.component').then(m => m.InboxComponent) },
      { path: 'order-links', loadComponent: () => import('./pages/order-links/order-links.component').then(m => m.OrderLinksComponent) },
      { path: 'rentals', loadComponent: () => import('./pages/rentals/rentals.component').then(m => m.RentalsComponent) },
      { path: 'social/onboarding', loadComponent: () => import('./pages/social/onboarding/social-onboarding.component').then(m => m.SocialOnboardingComponent) },
      // L'ancienne route `/social/posts` redirige vers `/marketing/posts` (Meta
      // Suite, couche 7) pour éviter le doublon de navigation. Tous les liens
      // historiques (sidebar, deep-links, partages) restent fonctionnels.
      { path: 'social/posts', redirectTo: '/marketing/posts', pathMatch: 'full' },
      { path: 'social/chatbot', loadComponent: () => import('./pages/social/chatbot/chatbot-settings.component').then(m => m.ChatbotSettingsComponent) },

      // ── Marketing (Meta Suite) — module unifié posts / reels / campaigns / leads / catalog / insights ──
      // marketing.routes.ts agrège toutes les sous-pages :
      // posts (couche 0, existant), reels-stories (couche 2),
      // campaigns (couche 3), leads (couche 4), catalog (couche 5),
      // insights (couche 6). Toutes ces couches sont mergées et réelles.
      {
        path: 'marketing',
        loadChildren: () =>
          import('./pages/marketing/marketing.routes').then((m) => m.MARKETING_ROUTES),
      },
      { path: 'modules', loadComponent: () => import('./pages/modules/modules-page.component').then(m => m.ModulesPageComponent) },

      // ── Centre d'aide /docs (guide vendeur.site) + recherche sémantique RAG ──
      // Coquille (recherche + nav catégories) + vues sommaire/article. Le
      // fragment #ancre est géré pour les citations de la bulle IA.
      {
        path: 'docs',
        loadChildren: () =>
          import('./pages/docs/docs.routes').then((m) => m.DOCS_ROUTES),
      },

      // ── Inventaire (dépôts, modeleur isométrique, mouvements, stock) ──
      { path: 'inventaire', loadComponent: () => import('./pages/inventory/inventory-list/inventory-list.component').then(m => m.InventoryListComponent) },
      { path: 'inventaire/stock', loadComponent: () => import('./pages/inventory/stock-overview/stock-overview.component').then(m => m.StockOverviewComponent) },
      { path: 'inventaire/depot/:id', loadComponent: () => import('./pages/inventory/warehouse-modeler/warehouse-modeler.component').then(m => m.WarehouseModelerComponent) },
      { path: 'inventaire/depot/:id/etiquettes', loadComponent: () => import('./pages/inventory/labels/labels.component').then(m => m.LabelsComponent) },
      { path: 'inventaire/mouvements', loadComponent: () => import('./pages/inventory/movements/movements-list.component').then(m => m.MovementsListComponent) },
      { path: 'scan', loadComponent: () => import('./pages/scan/scan-hub.component').then(m => m.ScanHubComponent) },
      { path: 'etiquettes', loadComponent: () => import('./pages/labels/labels-hub.component').then(m => m.LabelsHubComponent) },
      { path: 'settings', loadComponent: () => import('./pages/settings/settings/settings.component').then(m => m.SettingsComponent) },
      { path: 'settings/channels', loadComponent: () => import('./pages/settings/channels/channels.component').then(m => m.ChannelsSettingsComponent) },
      { path: 'settings/notifications', loadComponent: () => import('./pages/settings/notifications/notifications-settings.component').then(m => m.NotificationsSettingsComponent) },
      { path: 'settings/push', loadComponent: () => import('./pages/settings/push/push-settings.component').then(m => m.PushSettingsComponent) },
      { path: 'settings/api-keys', loadComponent: () => import('./pages/settings/api-keys/api-keys.component').then(m => m.ApiKeysComponent) },
      { path: 'settings/mail', loadComponent: () => import('./pages/settings/mail/mail-settings.component').then(m => m.MailSettingsComponent), data: { roles: ['vendor', 'admin'] } },
      { path: 'settings/whatsapp', loadComponent: () => import('./pages/settings/whatsapp/whatsapp-settings.component').then(m => m.WhatsappSettingsComponent), data: { roles: ['vendor', 'admin'] } },

      // ── Expédition (transporteurs Wassal / TunisiaShip) ──
      { path: 'expeditions', loadComponent: () => import('./pages/expeditions/expeditions.component').then(m => m.ExpeditionsComponent) },
      { path: 'expedition', loadComponent: () => import('./pages/settings/shipping/shipping-settings.component').then(m => m.ShippingSettingsComponent), data: { roles: ['vendor', 'admin'] } },
      { path: 'activities', loadComponent: () => import('./pages/activities/activities/activities.component').then(m => m.ActivitiesComponent) },

      // ── POS (point de vente) — mode JWT (vendeur/employé connecté) ──
      { path: 'pos', loadComponent: () => import('./pages/pos/pos-console/pos-console.component').then(m => m.PosConsoleComponent) },
      { path: 'pos/settings', loadComponent: () => import('./pages/pos/pos-settings/pos-settings.component').then(m => m.PosSettingsComponent), data: { roles: ['vendor', 'admin'] } },
      { path: 'pos/card-payments', loadComponent: () => import('./pages/pos/pos-card-payments/pos-card-payments.component').then(m => m.PosCardPaymentsComponent), data: { roles: ['vendor', 'admin'] } },

      // ── Équipe terrain (workforce agents) — routes statiques AVANT agents/:id ──
      { path: 'agents', loadComponent: () => import('./pages/agents/agents-list/agents-list.component').then(m => m.AgentsListComponent) },
      { path: 'agents/planning', loadComponent: () => import('./pages/agents/agents-planning/agents-planning.component').then(m => m.AgentsPlanningComponent) },
      { path: 'agents/roles', loadComponent: () => import('./pages/agents/agents-roles/agents-roles.component').then(m => m.AgentsRolesComponent) },
      { path: 'agents/messages', loadComponent: () => import('./pages/agents/agents-messages/agents-messages.component').then(m => m.AgentsMessagesComponent) },
      { path: 'agents/:id', loadComponent: () => import('./pages/agents/agent-profile/agent-profile.component').then(m => m.AgentProfileComponent) },

      // ── Téléphonie (centre d'appels multi-tenant : softphone + IVR + trunk) ──
      { path: 'telephonie', loadComponent: () => import('./pages/telephonie/telephonie-console.component').then(m => m.TelephonieConsoleComponent), data: { roles: ['vendor', 'admin'] } },

      // ── Tâches (Kanban + Gantt + perso) ──
      { path: 'tasks/board', loadComponent: () => import('./pages/tasks/kanban-board/kanban-board.component').then(m => m.KanbanBoardComponent) },
      { path: 'tasks/gantt', loadComponent: () => import('./pages/tasks/gantt-view/gantt-view.component').then(m => m.GanttViewComponent) },
      { path: 'me/tasks', loadComponent: () => import('./pages/tasks/my-tasks/my-tasks.component').then(m => m.MyTasksComponent) },

      // ── Intelligence (équipe IA + bras droit + MCP + sécurité) ──
      { path: 'ai-agents', loadComponent: () => import('./pages/ai-agents/ai-team/ai-team.component').then(m => m.AiTeamComponent), data: { roles: ['vendor', 'admin'] } },
      { path: 'ai-agents/recruitment', loadComponent: () => import('./pages/ai-agents/ai-recruitment/ai-recruitment.component').then(m => m.AiRecruitmentComponent), data: { roles: ['vendor', 'admin'] } },
      { path: 'ai-agents/right-hand', loadComponent: () => import('./pages/ai-agents/right-hand/right-hand.component').then(m => m.RightHandComponent), data: { roles: ['vendor', 'admin'] } },
      { path: 'ai-agents/marketer', loadComponent: () => import('./pages/ai-agents/marketer/marketer.component').then(m => m.MarketerComponent), data: { roles: ['vendor', 'admin'] } },
      { path: 'ai-agents/accountant', loadComponent: () => import('./pages/ai-agents/accountant/accountant.component').then(m => m.AccountantComponent), data: { roles: ['vendor', 'admin'] } },
      { path: 'ai-agents/mcp', loadComponent: () => import('./pages/ai-agents/mcp-servers/mcp-servers.component').then(m => m.McpServersComponent), data: { roles: ['vendor', 'admin'] } },
      { path: 'ai-agents/security', loadComponent: () => import('./pages/ai-agents/security-audit/security-audit.component').then(m => m.SecurityAuditComponent), data: { roles: ['vendor', 'admin'] } },

      // ── Dropshipping (Lot 1 — onboarding identité + signatures légales) ──
      // Wizard 5 étapes : profil (supplier/dropshipper/hybride), KYC,
      // sélection plan tarifaire, signature CGU + signature mandat
      // plateforme via mandat-sepa.fr (signature avancée OTP SMS).
      // Permissions vendor/admin ; activation effective après signatures.
      {
        path: 'dropship/onboarding',
        loadComponent: () =>
          import('./pages/dropship-onboarding/dropship-onboarding.component').then(
            (m) => m.DropshipOnboardingComponent,
          ),
        data: { roles: ['vendor', 'admin'] },
      },

      // ── Centre de support : helpdesk 2 niveaux + visios Google Meet ──
      {
        path: 'support',
        loadComponent: () =>
          import('./pages/support-center/support-center.component').then(
            (m) => m.SupportCenterComponent,
          ),
        data: { roles: ['vendor', 'admin'] },
      },

      // ── Supervision & controle plateforme (audit, sessions, telephonie, anti-fraude) ──
      {
        path: 'admin/supervision',
        loadComponent: () =>
          import('./pages/admin/supervision/supervision.component').then(
            (m) => m.SupervisionComponent,
          ),
        data: { roles: ['admin'] },
      },

      // ── Validation des dossiers vendeurs (onboarding) DANS l'app admin ──
      // Même console que /staff/onboarding (VPN), mais accessible aux rôles
      // admin/support depuis l'app normale : sans elle, les dossiers
      // pending_review restaient invisibles (aucun bouton « Approuver »).
      {
        path: 'admin/onboarding-review',
        loadComponent: () =>
          import('./pages/staff/staff-onboarding/staff-onboarding.component').then(
            (m) => m.StaffOnboardingComponent,
          ),
        data: { roles: ['admin', 'support'] },
      },

      // ── Base unifiée de prospection (visiteurs, scores, seuils notif) ──
      {
        path: 'admin/prospection',
        loadComponent: () =>
          import('./pages/staff/prospection/prospection.component').then(
            (m) => m.StaffProspectionComponent,
          ),
        data: { roles: ['admin', 'support'] },
      },

      // ── Lot 6 — Admin escrow dashboard (paiements, ledger, settlement) ──
      {
        path: 'admin/escrow',
        loadComponent: () =>
          import('./pages/admin-escrow/admin-escrow.component').then(
            (m) => m.AdminEscrowComponent,
          ),
        data: { roles: ['admin'] },
      },

      // ── Lot 6 — Dashboard dropshipping unifié supplier ↔ dropshipper ──
      {
        path: 'dropship/dashboard',
        loadComponent: () =>
          import('./pages/dropship-dashboard/dropship-dashboard.component').then(
            (m) => m.DropshipDashboardComponent,
          ),
        data: { roles: ['vendor', 'admin'] },
      },

      // ── Lot 2 FE — Partnership manager : annuaire suppliers + demandes ──
      {
        path: 'dropship/partnerships',
        loadComponent: () =>
          import('./pages/dropship-partnerships/dropship-partnerships.component').then(
            (m) => m.DropshipPartnershipsComponent,
          ),
        data: { roles: ['vendor', 'admin'] },
      },

      // ── Lot 2 FE — Politique dropshipping côté supplier (publication
      // dans l'annuaire). Sans cette page, un compte supplier reste
      // invisible aux dropshippers même après onboarding complet. ──
      {
        path: 'dropship/supplier-policy',
        loadComponent: () =>
          import('./pages/supplier-policy/supplier-policy.component').then(
            (m) => m.SupplierPolicyComponent,
          ),
        data: { roles: ['vendor', 'admin'] },
      },

      // ── Lot 2 FE — Catalog browser (par partnership) ──
      {
        path: 'dropship/catalog/:partnershipId',
        loadComponent: () =>
          import('./pages/dropship-catalog/dropship-catalog.component').then(
            (m) => m.DropshipCatalogComponent,
          ),
        data: { roles: ['vendor', 'admin'] },
      },

      // ── Lot 2 FE — Listing editor : create + edit ──
      {
        path: 'dropship/listings/new',
        loadComponent: () =>
          import('./pages/dropship-listing-editor/dropship-listing-editor.component').then(
            (m) => m.DropshipListingEditorComponent,
          ),
        data: { roles: ['vendor', 'admin'] },
      },
      {
        path: 'dropship/listings/:id',
        loadComponent: () =>
          import('./pages/dropship-listing-editor/dropship-listing-editor.component').then(
            (m) => m.DropshipListingEditorComponent,
          ),
        data: { roles: ['vendor', 'admin'] },
      },

      // ── Lot 2 FE — RFQ wizard 3-step ──
      {
        path: 'dropship/rfq/new',
        loadComponent: () =>
          import('./pages/dropship-rfq-wizard/dropship-rfq-wizard.component').then(
            (m) => m.DropshipRfqWizardComponent,
          ),
        data: { roles: ['vendor', 'admin'] },
      },

      // ── Site Builder vendeur.site ──
      { path: 'onboarding-site', loadComponent: () => import('./pages/site-onboarding/site-onboarding.component').then(m => m.SiteOnboardingComponent) },
      { path: 'sites', loadComponent: () => import('./pages/sites-hub/sites-hub.component').then(m => m.SitesHubComponent) },
      { path: 'sites/:id/studio', loadComponent: () => import('./pages/site-studio/site-studio.component').then(m => m.SiteStudioComponent) },
      { path: 'sites/:id/domains', loadComponent: () => import('./pages/site-domains/site-domains.component').then(m => m.SiteDomainsComponent) },

      // ── Administration (admin only — vérification supplémentaire dans le composant) ──
      {
        path: 'admin/ai-provider',
        loadComponent: () =>
          import('./pages/admin/ai-provider-settings/ai-provider-settings.component').then(
            (m) => m.AiProviderSettingsComponent,
          ),
      },
      {
        path: 'admin/api-keys',
        loadComponent: () =>
          import('./pages/admin/api-keys-admin/api-keys-admin.component').then(
            (m) => m.ApiKeysAdminComponent,
          ),
      },
      {
        path: 'admin/vendor-ai-settings',
        loadComponent: () =>
          import('./pages/admin/vendor-ai-settings/vendor-ai-settings.component').then(
            (m) => m.VendorAiSettingsComponent,
          ),
        data: { roles: ['admin'] },
      },
      {
        path: 'admin/blog',
        loadComponent: () =>
          import('./pages/admin/blog-admin/blog-admin.component').then(
            (m) => m.BlogAdminComponent,
          ),
        data: { roles: ['admin'] },
      },
      {
        path: 'admin/security',
        loadComponent: () =>
          import('./pages/admin/security-dashboard/security-dashboard.component').then(
            (m) => m.SecurityDashboardComponent,
          ),
        data: { roles: ['admin'] },
      },
    ]
  },

  // Route catch-all pour PageNotFound
  { path: '**', redirectTo: 'PageNotFound' },
];